import { createSlice, PayloadAction } from '@reduxjs/toolkit';
import { processRequest, RequestStatus } from './network/managedRequest';
import { API, API_SHORT } from '../constants';
import { PbDispatch, PbGetState } from './index';
import { PbRequest } from './pbRequest';

enum VatExemptionReason {
    VAT_EXEMPT = 'VAT_EXEMPT',
    REVERSE_CHARGE = 'REVERSE_CHARGE',
}

export interface Country {
    id: number;
    name: string;
    isocode1: string;
    isocode2: string;
    vat_percent: number;
}

interface CountryDetails {
    country_id: number;
    country_code: string;
    country_address_template: string;
    country_to_vat_exemption_reasons: VatExemptionReason[];
}

interface CountryDetailsByCode {
    [key: string]: CountryDetails;
}

export interface CountriesDetailsDto {
    countries_details: CountryDetailsByCode;
}

interface CountriesDetailsStore {
    idToCountry: { [key: number]: Country };
    countriesDetails?: CountriesDetailsDto;
    getCountries?: Country[];
}

const initialState: CountriesDetailsStore = {
    idToCountry: {},
    countriesDetails: undefined,
    getCountries: undefined,
};

export const countriesDetailsSlice = createSlice({
    name: 'countriesDetails',
    initialState,
    reducers: {
        setCountriesDetails(
            state: CountriesDetailsStore,
            action: PayloadAction<CountriesDetailsDto>
        ) {
            state.countriesDetails = action.payload;
        },
        setGetCountries(
            state: CountriesDetailsStore,
            action: PayloadAction<Country[]>
        ) {
            state.getCountries = action.payload;
        },
        storeCountries(
            state: CountriesDetailsStore,
            action: PayloadAction<Country[]>
        ) {
            const idToCountry = {};
            action.payload.forEach(
                (country) => (idToCountry[country.id] = country)
            );
            state.idToCountry = idToCountry;
        },
    },
});

export function LoadCountriesDetails() {
    return async (dispatch: PbDispatch) => {
        const response = await processRequest<CountriesDetailsDto>(
            dispatch,
            `${API}/country_specific_settings`,
            { method: 'GET' },
            PbRequest.GET_COUNTRIES_DETAILS
        );
        dispatch(countriesDetailsSlice.actions.setCountriesDetails(response));
    };
}

export function MaybeLoadCountries() {
    return async (dispatch: PbDispatch, getState: PbGetState) => {
        const currentStatus =
            getState().pbRequest[PbRequest.GET_COUNTRIES].status;
        if (
            [RequestStatus.PENDING, RequestStatus.SUCCESS].includes(
                currentStatus
            )
        ) {
            return;
        }
        try {
            const response = await processRequest<Country[]>(
                dispatch,
                `${API_SHORT}/v1/countries`,
                { method: 'GET' },
                PbRequest.GET_COUNTRIES
            );
            dispatch(countriesDetailsSlice.actions.setGetCountries(response));
            dispatch(countriesDetailsSlice.actions.storeCountries(response));
        } catch (e) {
            console.log('failed to load countries', e);
        }
    };
}

export default countriesDetailsSlice.reducer;
